import React from 'react';
import moment from 'moment';
import { formatTimeOnlyShort } from '../../../utils/time';

import IntervalInformationPopup from './IntervalInformationPopup';
import AvailabilityCodeIcon from '../../general/AvailabilityCodeIcon';
import { Tooltip, ProgressBar } from '../../ui-kit';
import { IconExclamationTriangle } from '../../ui-kit/icons';

export const displayIntervals = (intervals, periodStartTime, periodEndTime) => {
  const result = [];
  let currentTime = periodStartTime;

  const periodLength = moment(periodEndTime).diff(periodStartTime, 'seconds');
  const createInterval = (
    start_time,
    end_time,
    available = false,
    functionId = null,
    comments = '',
    availabilityCode = null
  ) => {
    // Pick a color based on availability status, these colors match the group rota representation
    let color = 'lightgrey';
    if (available) color = functionId ? 'blue' : 'green';
    if (availabilityCode && !availabilityCode.available_for_crewing) color = 'red';
    const width = (moment(end_time).diff(start_time, 'seconds') / periodLength) * 100;
    return {
      start_time,
      end_time,
      width,
      color,
      comments,
      availabilityCode
    };
  };

  for (const interval of intervals) {
    if (moment(currentTime).isBefore(interval.start_time)) {
      result.push(createInterval(currentTime, interval.start_time, false, interval.assigned_function_ids.length > 0));
    }
    result.push(
      createInterval(
        interval.start_time,
        interval.end_time,
        true,
        interval.assigned_function_ids.length > 0,
        interval.comments,
        interval.availabilityCode
      )
    );
    currentTime = interval.end_time;
  }

  if (moment(periodEndTime).isAfter(intervals.at(-1).end_time)) {
    result.push(createInterval(intervals.at(-1).end_time, periodEndTime, false, null));
  }

  return result.map(interval => (
    <ProgressBar
      key={moment(interval.start_time).format()}
      now={interval.width}
      style={{ backgroundColor: interval.color }}
      title={`${formatTimeOnlyShort(interval.start_time)}-${formatTimeOnlyShort(
        interval.end_time
      )} ${interval.comments}`}
    />
  ));
};

// Utility method for calculating the start and end time of an item while it
// is being moved or resized. This is used for collision detecting in the
// validator, and to display the start and end times of the interval while
// dragging.
const calculateItemPeriod = (item, time, dragging, resizeEdge) => {
  if (dragging) {
    // when dragging, time is start time of the interval. End time is
    // calculated by adding the period
    const periodLength = moment(item.end_time).diff(item.start_time);
    return { startTime: moment(time), endTime: moment(time).add(periodLength) };
  } else if (resizeEdge === 'left') {
    // resizing on the left. `time` param is new start time
    return { startTime: moment(time), endTime: moment(item.end_time) };
  } else {
    // resizing on the right. `time` param is new end time
    return { startTime: moment(item.start_time), endTime: moment(time) };
  }
};

const IntervalEdge = props => (
  <div {...props.resizeProps}>
    {props.shouldDisplayTime && (
      <Tooltip
        style={{
          marginTop: '10px',
          width: '4rem'
        }}
        placement={'auto'}
        className="in show"
        id={`tooltip-${props.tooltipPlacement}`}
      >
        {formatTimeOnlyShort(props.time)}
      </Tooltip>
    )}
  </div>
);

// This method is responsible for rendering the actual intervals. Currently,
// its functionality is focused on rendering intervals in the right color.
// This is fairly rudimentary for now. It only renders standby duty correctly.
// It is not yet capable of also rendering availability. In the context of the
// DutyPlanner, this is not an issue. For the group rota, however, changes
// will need to be made. Also, a lot of this functionality will need to be
// moved to more general files.
export const itemRenderer = ({ item, itemContext, getItemProps, getResizeProps }) => {
  const { left: leftResizeProps, right: rightResizeProps } = getResizeProps();
  const defaultProps = getItemProps(item.itemProps);

  let colors = null;
  const standbyDuty = item.assigned_function_ids.length > 0;
  const availableForCrewing = !item.availabilityCode || item.availabilityCode.available_for_crewing;
  if (standbyDuty && availableForCrewing) {
    colors = {
      background: '#5959fc',
      border: '2px solid blue',
      color: 'white'
    };
  } else if (standbyDuty && !availableForCrewing) {
    colors = {
      background: 'white',
      border: '2px solid blue',
      color: 'black'
    };
  }

  if (item.annotations.auto_seat_allocation_suggestion) {
    colors = {
      background: 'lightblue',
      border: '2px dashed blue',
      color: 'black'
    };
  }

  let border = colors.border;
  if (item.transient && !item.annotations.auto_seat_allocation_suggestion) {
    border = '2px dashed lightblue';
  } else if (itemContext.selected) {
    border = '2px solid #dba800';
  }

  const itemProps = {
    ...defaultProps,
    style: {
      ...defaultProps.style,
      ...colors,
      border
    }
  };

  const { dragging, dragTime, resizeEdge, resizeTime } = itemContext;

  const { startTime, endTime } = calculateItemPeriod(item, dragTime || resizeTime, dragging, resizeEdge);

  const shouldDisplayTime = itemContext.dragging || itemContext.resizing;
  // We append custom styling for the left and right resize
  // handlers. When rezising, a pointer with two arrows should be
  // Displayed. We also move the handler some pixels to the left and right,
  // otherwise there is a bit of margin displaying the original cursor
  const selectedLeftResizeProps = {
    ...leftResizeProps,
    style: {
      ...leftResizeProps.style,
      cursor: 'ew-resize',
      left: '-3px'
    }
  };

  const selectedRightResizeProps = {
    ...rightResizeProps,
    style: {
      ...rightResizeProps.style,
      cursor: 'ew-resize',
      right: '-3px'
    }
  };
  /**
   * changed to itemContext.selected because the previous value
   * itemContext.useResizeHandle was always false and therefore
   * the div for handles was never rendering the custom props.
   * When using this value we can know if we are ready to modify
   * the interval's size.
   */
  return (
    /**
     * The interval is now wrapped inside an IntervalInformationPopup
     * that acts as a hoverable wrapper that renders the children
     * (the interval) in the timeline if applicable and a popup with
     * its details.
     */
    <IntervalInformationPopup
      alwaysDisplayTimes={true}
      overlayPlacement={'top'}
      intervals={[item]}
      dutyTransactions={item.dutyTransactions}
    >
      <div {...itemProps}>
        {itemContext.selected && (
          <IntervalEdge
            tooltipPlacement={'right'}
            resizeProps={selectedLeftResizeProps}
            shouldDisplayTime={shouldDisplayTime}
            time={startTime}
          />
        )}
        <div className="rct-item-content" style={{ maxHeight: `${itemContext.dimensions.height}` }}>
          {item.userNickname} <AvailabilityCodeIcon code={item.availabilityCode} />{' '}
          {item.dutyTransactions.length > 0 && <IconExclamationTriangle className="text-warning" />}
        </div>
        {itemContext.selected && (
          <IntervalEdge
            tooltipPlacement={'left'}
            resizeProps={selectedRightResizeProps}
            shouldDisplayTime={shouldDisplayTime}
            time={endTime}
          />
        )}
      </div>
    </IntervalInformationPopup>
  );
};
